/** Exclusive action families for smart sprite candidate filtering. */

export const ACTION_FAMILIES = Object.freeze({
  AFTER_SEX: 'after_sex',
  FOREPLAY: 'foreplay',
  SEX: 'sex',
  BASE: 'base',
  MISC_SFW: 'misc_sfw',
  MISC_NSFW: 'misc_nsfw'
});

const FAMILY_SET = new Set(Object.values(ACTION_FAMILIES));

const SEX_ACT_RE = /\b(?:sex|cowgirl|doggystyle|doggy style|missionary|fellatio|cunnilingus|paizuri|irrumatio|gangbang|spooning|deepthroat|buttjob|handjob|footjob|vaginal|anal|penetration|congress)\b/;
const GENITAL_RE = /\b(?:clitoris|pussy|crotch|penis)\b/;
const FOREPLAY_RE = /\b(?:imminent|just the tip)\b|\bbefore\b/;
const SPECIALTY_NSFW_RE = /\b(?:slime|tentacle|dildo|vibrator|sex toy|sextoy)\b/;
const AFTER_SCENE_RE = /\bafter(?:\s+sex|\s+vaginal|\s+facial)\b|\bpull out\b|\bbukkake\b|\bvomiting cum\b/;
const CUM_RE = /\b(?:cum|ejaculat)/;
const SEXUAL_LEFTOVER_RE = /\b(?:nipple|undress|panty|panties|nude|flashing|masturbat|fingering|aroused|orgasm|moaning|breasts?|explicit)\b/;
const STANDING_RE = /\bstanding\b/;
const IDLE_SFW_RE = /\b(?:cowboy shot|solo)\b/;

const normalizeFamilyHaystack = (prompt, name) => {
  const source = String(prompt || '').trim() || String(name || '');
  return source
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizeActionFamily = (value) => {
  const family = String(value || '').toLowerCase().trim();
  if (family === 'climax') return ACTION_FAMILIES.AFTER_SEX;
  return FAMILY_SET.has(family) ? family : null;
};

const hasSexualTokens = (hay) => (
  SEX_ACT_RE.test(hay)
  || GENITAL_RE.test(hay)
  || FOREPLAY_RE.test(hay)
  || SPECIALTY_NSFW_RE.test(hay)
  || AFTER_SCENE_RE.test(hay)
  || CUM_RE.test(hay)
  || SEXUAL_LEFTOVER_RE.test(hay)
);

export const classifyActionFamily = ({ prompt = '', name = '', isNsfw = false } = {}) => {
  const hay = normalizeFamilyHaystack(prompt, name);
  if (!hay) return isNsfw ? ACTION_FAMILIES.MISC_NSFW : ACTION_FAMILIES.MISC_SFW;

  if (AFTER_SCENE_RE.test(hay)) return ACTION_FAMILIES.AFTER_SEX;

  if (FOREPLAY_RE.test(hay)) return ACTION_FAMILIES.FOREPLAY;
  if (SPECIALTY_NSFW_RE.test(hay)) return ACTION_FAMILIES.MISC_NSFW;
  if (GENITAL_RE.test(hay) && !SEX_ACT_RE.test(hay)) return ACTION_FAMILIES.FOREPLAY;

  if (SEX_ACT_RE.test(hay)) return ACTION_FAMILIES.SEX;

  if (!hasSexualTokens(hay) && (STANDING_RE.test(hay) || IDLE_SFW_RE.test(hay))) {
    return ACTION_FAMILIES.BASE;
  }

  if (isNsfw || SEXUAL_LEFTOVER_RE.test(hay) || GENITAL_RE.test(hay) || CUM_RE.test(hay)) {
    return ACTION_FAMILIES.MISC_NSFW;
  }

  return ACTION_FAMILIES.MISC_SFW;
};

const hashSeed = (value) => {
  let hash = 2166136261;
  const text = String(value ?? '');
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const mulberry32 = (seed) => {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6D2B79F5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const dropHalfBySeed = (items, seed) => {
  const list = Array.isArray(items) ? [...items] : [];
  const dropped = new Set();
  if (list.length < 2) return dropped;
  const rng = mulberry32(hashSeed(seed));
  for (let i = list.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const swap = list[i];
    list[i] = list[j];
    list[j] = swap;
  }
  const dropCount = Math.floor(list.length / 2);
  list.slice(0, dropCount).forEach((item) => dropped.add(item));
  return dropped;
};

const familyOf = (name, getFamily) => {
  if (typeof getFamily !== 'function') return null;
  return normalizeActionFamily(getFamily(name));
};

export const applySmartFamilyFilter = (orderedNames, {
  currentFamily,
  getFamily,
  seed = ''
} = {}) => {
  const names = Array.isArray(orderedNames) ? orderedNames : [];
  if (names.length === 0) return names;

  const current = normalizeActionFamily(currentFamily) || ACTION_FAMILIES.BASE;

  const keepUnknownOr = (name, predicate) => {
    const family = familyOf(name, getFamily);
    if (!family) return true;
    return predicate(family);
  };

  let filtered = names;
  if (current === ACTION_FAMILIES.BASE || current === ACTION_FAMILIES.MISC_SFW) {
    filtered = names.filter((name) => keepUnknownOr(name, (family) => (
      family !== ACTION_FAMILIES.SEX && family !== ACTION_FAMILIES.AFTER_SEX
    )));
  } else if (current === ACTION_FAMILIES.FOREPLAY) {
    const withoutClimax = names.filter((name) => keepUnknownOr(name, (family) => family !== ACTION_FAMILIES.AFTER_SEX));
    const baseItems = withoutClimax.filter((name) => familyOf(name, getFamily) === ACTION_FAMILIES.BASE);
    const droppedBase = dropHalfBySeed(baseItems, seed);
    filtered = withoutClimax.filter((name) => !droppedBase.has(name));
  } else if (current === ACTION_FAMILIES.SEX) {
    filtered = names.filter((name) => keepUnknownOr(name, (family) => (
      family !== ACTION_FAMILIES.BASE && family !== ACTION_FAMILIES.MISC_SFW
    )));
  } else if (current === ACTION_FAMILIES.AFTER_SEX || current === ACTION_FAMILIES.MISC_NSFW) {
    filtered = names.filter((name) => keepUnknownOr(name, (family) => (
      family !== ACTION_FAMILIES.MISC_SFW && family !== ACTION_FAMILIES.SEX
    )));
  }

  return filtered.length > 0 ? filtered : names;
};
